import { useEffect, useRef } from "react";

import config from "@/config";
import fetchWrapper from "@/services/fetchWrapper.service";
import { resolveArtworkViewSource } from "@/shared/lib/artworkViewSource";

import {
  isLikelyMongoObjectId,
  matchesArtworkRouteContext,
} from "./artworkFeedState.utils";
import {
  ARTWORK_ROUTING_DEBUG_STORAGE_KEY,
  isArtworkRoutingDebugEnabled,
} from "./artworkRoutingDebug.utils";

export default function useArtworkVisitTelemetry({
  location,
  slug,
  activeSlide,
  artworks,
  listCacheKeyRef,
  holdingId,
  holdingOwnerId,
  holdingTypeParam,
}) {
  const recordedVisitsRef = useRef(new Set());
  const routingDebugSeqRef = useRef(0);
  const lastUrlRef = useRef("");

  function logRouting(event, payload = {}) {
    if (!isArtworkRoutingDebugEnabled()) {
      return;
    }

    routingDebugSeqRef.current += 1;
    console.log(`[ARTWORK_ROUTING][${routingDebugSeqRef.current}] ${event}`, {
      at: new Date().toISOString(),
      url: `${location.pathname}${location.search || ""}`,
      slug,
      activeSlide,
      listLength: artworks?.data?.length ?? 0,
      cacheKey: listCacheKeyRef.current ?? location.state?.cacheKey ?? null,
      ...payload,
    });
  }

  useEffect(() => {
    if (isArtworkRoutingDebugEnabled()) {
      console.info("[ARTWORK_ROUTING] debug-enabled", {
        storageKey: ARTWORK_ROUTING_DEBUG_STORAGE_KEY,
        url: `${location.pathname}${location.search || ""}`,
      });
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const currentUrl = `${location.pathname}${location.search || ""}`;
    if (lastUrlRef.current !== currentUrl) {
      logRouting("location-change", {
        from: lastUrlRef.current || null,
        to: currentUrl,
      });
      lastUrlRef.current = currentUrl;
    }
  }, [location.pathname, location.search]);

  function resolveVisitSourcePayload(slugValue) {
    const sourceArtwork = Array.isArray(artworks?.data)
      ? artworks.data.find((artwork) => matchesArtworkRouteContext(artwork, {
        slug: slugValue,
        holdingId,
        ownerId: holdingOwnerId,
        holdingType: holdingTypeParam,
      }))
      : null;
    const resolvedSource = resolveArtworkViewSource({
      itemSource: sourceArtwork?._artworkViewSource,
      navigationSource: location.state?.artworkViewSource,
      pathname: location.pathname,
    });

    if (!resolvedSource) {
      return null;
    }

    return {
      source: resolvedSource.type ?? resolvedSource.label ?? undefined,
      continuation: resolvedSource.continuation ?? undefined,
    };
  }

  function postVisitOnce(slugValue) {
    if (!slugValue) {
      logRouting("visit-skip-empty-slug");
      return;
    }
    if (isLikelyMongoObjectId(slugValue)) {
      logRouting("visit-skip-object-id", { visitSlug: slugValue });
      return;
    }
    if (recordedVisitsRef.current.has(slugValue)) {
      logRouting("visit-skip-duplicate", { visitSlug: slugValue });
      return;
    }
    recordedVisitsRef.current.add(slugValue);
    logRouting("visit-post", { visitSlug: slugValue });
    const visitSourcePayload = resolveVisitSourcePayload(slugValue);
    fetchWrapper.post(`${config.apis.api.url}/artwork/visit/${slugValue}`, {
      injectToken: true,
      body: visitSourcePayload ?? undefined,
    }).catch(() => {
      logRouting("visit-post-failed", { visitSlug: slugValue });
      recordedVisitsRef.current.delete(slugValue);
    });
  }

  return {
    logRouting,
    postVisitOnce,
  };
}
